import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import type { AuthState } from './hooks/useAuth';
import { ErrorBoundary } from './components/ErrorBoundary';
import { registerServiceWorker } from './lib/pwa-update';
import { bootstrapDefaultServers } from './lib/bootstrap-servers';
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root element missing in index.html');

// Sprint access-session-recovery (challenger R1 change 5's corollary) —
// `LoginPage`'s re-entry button (D3) navigates to `/?reauth=<ts>` so the
// precached shell is bypassed and the Access gate can complete. Once
// `useAuth` resolves to anything other than `gate-expired` the param has
// done its job: leaving it in the URL bar (and in any bookmark/share of
// it) is clutter with no further purpose. Strips it ONLY on that
// resolution — never eagerly — so a REPEAT `gate-expired` resolution
// (the EC-3 signature `LoginPage` reads via `useSearchParams`) still sees
// it.
function stripReauthParam(): void {
  if (!window.location.search.includes('reauth=')) return;
  const url = new URL(window.location.href);
  url.searchParams.delete('reauth');
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

function onAuthResolved(state: AuthState): void {
  if (state === 'unknown' || state === 'gate-expired') return;
  stripReauthParam();
}

// Sprint 11b post-flip catch-22 fix — pre-seed the multi-server registry
// from the public manifest BEFORE first render, so `getActiveBaseUrl()`
// returns the canonical backend URL on the very first `/api/auth/me`
// call. ~50-100 ms in the common case (same-origin static asset);
// hard-capped at 2 s by the helper so a network blackhole degrades
// gracefully instead of hanging the boot indefinitely. Wrapped in an
// async IIFE because top-level await would push the build target above
// es2020 (current floor).
void (async () => {
  await bootstrapDefaultServers();

  createRoot(rootEl).render(
    <StrictMode>
      {/* S7.4 — outermost ErrorBoundary. Without this, ANY uncaught render
          exception unmounts the whole tree and the founder sees a black
          page with no recovery affordance. */}
      <ErrorBoundary surface="root">
        <BrowserRouter>
          <App onAuthResolved={onAuthResolved} />
        </BrowserRouter>
      </ErrorBoundary>
    </StrictMode>,
  );

  // Sprint 7 (Phase 5) — register the PWA service worker. No-ops in dev
  // and on browsers without SW support. When a new version is detected,
  // the page surfaces an UpdateAvailableToast (handled in pwa-update.ts).
  registerServiceWorker();
})();
